import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { MdCheckCircle, MdChevronLeft, MdChevronRight } from "react-icons/md";
import { getApiErrorMessage } from "api";
import AlertPane from "components/alert";
import Button, { IconButton } from "components/button";
import Loader from "components/loader/Loader";
import { ErrorState } from "components/states";
import notify from "lib/notify";
import heartbeatSurveyService from "services/heartbeatSurveyService";
import PublicSurveyShell from "views/public/shared/PublicSurveyShell";
import SurveyProgress from "views/public/shared/SurveyProgress";
import ContactOptIn from "./components/ContactOptIn";
import IconTile from "./components/IconTile";
import PrayerRequestCard from "./components/PrayerRequestCard";
import SurveyQuestion from "./components/SurveyQuestion";
import SurveyResults from "./components/SurveyResults";
import SurveyWelcome from "./components/SurveyWelcome";

const EMPTY_CONTACT = { firstName: "", lastName: "", email: "", phone: "" };

const joinName = (...parts) => parts.filter(Boolean).join(" ").trim();

/** Slider questions start at the midpoint so "Continue" works without touching them. */
const initialAnswersFor = (questions = []) =>
  questions.reduce((acc, q) => {
    if (q.type === "slider") acc[q.id] = 3;
    return acc;
  }, {});

const isQuestionAnswered = (question, answer) => {
  if (!question?.required) return true;
  if (answer === undefined || answer === null) return false;
  if (typeof answer === "string" && answer.trim() === "") return false;
  if (Array.isArray(answer) && answer.length === 0) return false;
  return true;
};

const PreviewBanner = () => (
  <div className="bg-warning-subtle px-4 py-2 text-center text-sm font-semibold text-warning-fg">
    Preview mode: this is how recipients see the survey. Responses are not recorded.
  </div>
);

/**
 * Mobile-first heartbeat survey, opened from a QR code (/survey/qr/:qrToken) or an
 * invitation link (/survey/:token).
 *
 * Flow: loading → name (welcome) → questions → submitting → completed → contact → done.
 *
 * Theme: spiritual and general surveys keep their immersive dark look by forcing the
 * dark token scope on the page root; Pathways surveys follow the app theme.
 */
const SpiritualSurveyPage = () => {
  const { qrToken, token } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "true";
  // Invitation mode: loaded from /survey/:token (email/SMS link)
  const isInvitationMode = !!token && !qrToken;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  const [flowState, setFlowState] = useState("loading"); // loading | error | name | questions | submitting | completed | contact | done
  const [visitorName, setVisitorName] = useState("");
  const [surveyToken, setSurveyToken] = useState(null);

  // Welcome screen contact fields (expanded on request for autocomplete)
  const [showExpandedFields, setShowExpandedFields] = useState(false);
  const [welcomeContact, setWelcomeContact] = useState(EMPTY_CONTACT);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [startTime, setStartTime] = useState(null);
  const [slideDirection, setSlideDirection] = useState("right");
  const [isAnimating, setIsAnimating] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const [result, setResult] = useState(null);

  const [contactInfo, setContactInfo] = useState({ ...EMPTY_CONTACT, requestFollowUp: false });
  const [contactSubmitting, setContactSubmitting] = useState(false);

  // 30-day devotional email signup
  const [emailSignupSubmitted, setEmailSignupSubmitted] = useState(false);
  const [emailSignupData, setEmailSignupData] = useState({ email: "", firstName: "", lastName: "" });

  // Prayer request (campaigns with enablePrayerRequests)
  const [showPrayerForm, setShowPrayerForm] = useState(false);
  const [prayerRequest, setPrayerRequest] = useState("");
  const [prayerSubmitted, setPrayerSubmitted] = useState(false);
  const [prayerSubmitting, setPrayerSubmitting] = useState(false);
  const [prayerContactInfo, setPrayerContactInfo] = useState(EMPTY_CONTACT);

  const fetchSurveyByToken = useCallback(async () => {
    try {
      setLoading(true);
      const response = await heartbeatSurveyService.getSurveyByToken(token);

      if (response.success) {
        setCampaign(response.campaign);
        setOrganization(response.organization);
        setSurveyToken(token); // the invitation token is the survey token; no startQrSurvey needed

        if (response.user?.firstName) {
          setVisitorName(response.user.firstName);
          setWelcomeContact((prev) => ({
            ...prev,
            firstName: response.user.firstName,
            lastName: response.user.lastName || "",
          }));
        }

        setAnswers(initialAnswersFor(response.campaign?.questions));
        setFlowState("name");
      } else {
        setError({ title: "Survey Unavailable", message: response.message || "Unable to load survey." });
        setFlowState("error");
      }
    } catch (err) {
      console.error("Error fetching invitation survey:", err);
      const status = err.response?.status;
      if (status === 409) {
        setError({ title: "Survey Already Completed", message: "You've already completed this survey. Thank you." });
      } else if (status === 410) {
        setError({ title: "Link Expired", message: "This survey link has expired." });
      } else if (status === 404) {
        setError({ title: "Survey Unavailable", message: "This survey link is no longer valid." });
      } else {
        setError({ message: "Unable to load survey. Please try again later.", retryable: true });
      }
      setFlowState("error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchSurveyByQrCode = useCallback(async () => {
    try {
      setLoading(true);
      const response = await heartbeatSurveyService.getSurveyByQrCode(qrToken);

      if (response.success) {
        setCampaign(response.data.campaign);
        setOrganization(response.data.organization);
        setSessionId(response.data.sessionId);
        setAnswers(initialAnswersFor(response.data.campaign.questions));
        setFlowState("name");
      } else {
        setError({ title: "Survey Unavailable", message: response.message || "Unable to load survey." });
        setFlowState("error");
      }
    } catch (err) {
      console.error("Error fetching survey:", err);
      if (err.response?.status === 404) {
        setError({
          title: "Survey Unavailable",
          message: "This survey is not available or has been deactivated.",
        });
      } else {
        setError({ message: "Unable to load survey. Please try again later.", retryable: true });
      }
      setFlowState("error");
    } finally {
      setLoading(false);
    }
  }, [qrToken]);

  const loadSurvey = useCallback(() => {
    setError(null);
    setFlowState("loading");
    if (isInvitationMode) {
      fetchSurveyByToken();
    } else if (qrToken) {
      fetchSurveyByQrCode();
    }
  }, [isInvitationMode, qrToken, fetchSurveyByToken, fetchSurveyByQrCode]);

  useEffect(() => {
    loadSurvey();
  }, [loadSurvey]);

  // Pre-fill the follow-up forms from the welcome screen once results are shown.
  useEffect(() => {
    if (flowState !== "completed") return;
    const firstName = welcomeContact.firstName || visitorName;

    setEmailSignupData((prev) => ({
      ...prev,
      firstName: firstName || prev.firstName,
      lastName: welcomeContact.lastName || prev.lastName,
      email: welcomeContact.email || prev.email,
    }));

    const prefillContact = (prev) => ({
      ...prev,
      firstName: firstName || prev.firstName,
      lastName: welcomeContact.lastName || prev.lastName,
      email: welcomeContact.email || prev.email,
      phone: welcomeContact.phone || prev.phone,
    });
    setContactInfo(prefillContact);
    setPrayerContactInfo(prefillContact);
  }, [flowState, visitorName, welcomeContact]);

  const handleWelcomeContactChange = (field, value) => {
    setWelcomeContact((prev) => ({ ...prev, [field]: value }));
    if (field === "firstName") setVisitorName(value);
  };

  const handleStartSurvey = async () => {
    // Preview mode never creates records; jump straight to the questions.
    if (isPreview) {
      if (!isInvitationMode) setSurveyToken("preview");
      setStartTime(Date.now());
      setFlowState("questions");
      return;
    }

    // Invitation mode already has its token.
    if (isInvitationMode) {
      setStartTime(Date.now());
      setFlowState("questions");
      return;
    }

    try {
      setLoading(true);
      // QR mode: creates the anonymous respondent and invitation.
      const response = await heartbeatSurveyService.startQrSurvey(qrToken, sessionId, visitorName || null);

      if (response.success) {
        setSurveyToken(response.data.token);
        setStartTime(Date.now());
        setFlowState("questions");
      } else {
        notify.error("Failed to start survey. Please try again.");
      }
    } catch (err) {
      console.error("Error starting survey:", err);
      notify.error(getApiErrorMessage(err, "Failed to start survey. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    if (validationError) setValidationError(null);
  };

  const questions = campaign?.questions || [];
  const currentQ = questions[currentQuestion];

  const handleSubmit = async () => {
    // Preview mode shows the completed screen without submitting.
    if (isPreview) {
      setFlowState("completed");
      return;
    }

    try {
      setFlowState("submitting");
      setSubmitting(true);

      const timeToComplete = startTime ? Math.floor((Date.now() - startTime) / 1000) : null;
      const fullName = joinName(welcomeContact.firstName, welcomeContact.lastName) || visitorName || null;
      const hasContactInfo = !!(welcomeContact.email || welcomeContact.phone);

      // Invitation mode uses the simpler submit endpoint (the person is already known).
      const response = isInvitationMode
        ? await heartbeatSurveyService.submitSurvey(surveyToken, answers, timeToComplete)
        : await heartbeatSurveyService.submitSpiritualSurvey(surveyToken, {
            answers,
            timeToComplete,
            sessionId,
            isAnonymous: false,
            followUpRequested: hasContactInfo,
            followUpName: fullName,
            followUpEmail: welcomeContact.email || null,
            followUpPhone: welcomeContact.phone || null,
          });

      if (response.success) {
        setResult(response.data);
        setFlowState("completed");
      } else {
        notify.error("Failed to submit survey. Please try again.");
        setFlowState("questions");
      }
    } catch (err) {
      console.error("Error submitting survey:", err);
      notify.error(getApiErrorMessage(err, "Failed to submit survey. Please try again."));
      setFlowState("questions");
    } finally {
      setSubmitting(false);
    }
  };

  const slideTo = (nextIndex, direction) => {
    setIsAnimating(true);
    setSlideDirection(direction);
    setTimeout(() => {
      setCurrentQuestion(nextIndex);
      setSlideDirection(direction === "left" ? "right" : "left");
      setTimeout(() => setIsAnimating(false), 50);
    }, 200);
  };

  const handleNext = () => {
    if (isAnimating || submitting) return;

    if (!isQuestionAnswered(currentQ, answers[currentQ?.id])) {
      setValidationError("This question requires an answer");
      return;
    }
    setValidationError(null);

    if (currentQuestion < questions.length - 1) {
      slideTo(currentQuestion + 1, "left");
    } else {
      handleSubmit();
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0 && !isAnimating) slideTo(currentQuestion - 1, "right");
  };

  const handleContactOptIn = async () => {
    if (!contactInfo.email && !contactInfo.phone) return;
    setContactSubmitting(true);
    try {
      await heartbeatSurveyService.updateFollowUpInfo(surveyToken, {
        email: contactInfo.email || null,
        phone: contactInfo.phone || null,
        name: joinName(contactInfo.firstName, contactInfo.lastName) || visitorName || null,
        requestFollowUp: true,
      });
    } catch (err) {
      // The respondent is done either way; the follow-up request is best effort.
      console.error("Error saving contact info:", err);
    } finally {
      setContactSubmitting(false);
    }
    setFlowState("done");
  };

  // Returns to the results screen from the pastoral care prompt.
  const handleSkipContact = () => setFlowState("completed");

  // 30-day devotional signup: fire-and-forget so the confirmation shows instantly.
  const handleEmailSignup = () => {
    if (!emailSignupData.email || !emailSignupData.firstName) return;

    const signupData = {
      email: emailSignupData.email,
      firstName: emailSignupData.firstName,
      lastName: emailSignupData.lastName || "",
      surveyScores: result?.scores || {},
      overallScore: result?.overallScore || 0,
      responseId: result?.responseId || result?.id,
      campaignId: campaign?.id,
      organizationId: organization?.id,
      campaignType: "spiritual",
    };

    setEmailSignupSubmitted(true);

    const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8080";
    fetch(`${baseUrl}/api/v2/heartbeat/survey/devotional-signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(signupData),
    })
      .then((response) => response.json())
      .then((body) => {
        if (!body.success) console.warn("Devotional signup warning:", body.message);
      })
      .catch((err) => {
        console.error("Error submitting devotional signup:", err);
        // Keep a local copy so the signup is not lost.
        const existingSignups = JSON.parse(localStorage.getItem("devotional_signups") || "[]");
        existingSignups.push({ ...signupData, signupDate: new Date().toISOString(), fallback: true });
        localStorage.setItem("devotional_signups", JSON.stringify(existingSignups));
      });
  };

  const handlePrayerSubmit = async () => {
    if (!prayerRequest.trim()) return;

    setPrayerSubmitting(true);

    const prayerData = {
      organizationId: organization?.id,
      campaignId: campaign?.id,
      responseId: result?.responseId || result?.id,
      firstName: prayerContactInfo.firstName || null,
      lastName: prayerContactInfo.lastName || null,
      name: joinName(prayerContactInfo.firstName, prayerContactInfo.lastName) || visitorName || "Anonymous",
      email: prayerContactInfo.email || null,
      phone: prayerContactInfo.phone || null,
      request: prayerRequest,
      source: "survey",
    };

    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8080";
      const response = await fetch(`${baseUrl}/api/v2/heartbeat/survey/prayer-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(prayerData),
      });

      const data = await response.json();
      if (data.success) {
        setPrayerSubmitted(true);
        setShowPrayerForm(false);
      }
    } catch (err) {
      // Confirm to the respondent anyway; the request is logged for follow-up.
      console.error("Error submitting prayer request:", err);
      setPrayerSubmitted(true);
      setShowPrayerForm(false);
    } finally {
      setPrayerSubmitting(false);
    }
  };

  // Pathways surveys (surveyType "pathways_*") use the app theme and simpler copy.
  const isPathwaysSurvey = campaign?.surveyType?.startsWith("pathways_");
  const shellProps = {
    dark: !isPathwaysSurvey,
    organizationName: organization?.name,
    organizationLogo: organization?.logoUrl,
    banner: isPreview ? <PreviewBanner /> : null,
  };

  if (flowState === "loading") {
    return (
      <PublicSurveyShell {...shellProps} center>
        <Loader size="lg" label="Loading your survey" />
      </PublicSurveyShell>
    );
  }

  if (flowState === "error") {
    return (
      <PublicSurveyShell {...shellProps} center>
        <ErrorState
          title={error?.title}
          titleAs="h1"
          message={error?.message}
          onRetry={error?.retryable ? loadSurvey : undefined}
        />
      </PublicSurveyShell>
    );
  }

  if (flowState === "name") {
    return (
      <PublicSurveyShell {...shellProps} center>
        <SurveyWelcome
          campaign={campaign}
          isPathwaysSurvey={isPathwaysSurvey}
          visitorName={visitorName}
          contact={welcomeContact}
          onContactChange={handleWelcomeContactChange}
          showContactFields={showExpandedFields}
          onShowContactFields={() => setShowExpandedFields(true)}
          starting={loading}
          onStart={handleStartSurvey}
        />
      </PublicSurveyShell>
    );
  }

  if (flowState === "questions" && currentQ) {
    const slideClass = !isAnimating
      ? "translate-x-0 opacity-100"
      : slideDirection === "left"
      ? "-translate-x-8 opacity-0"
      : "translate-x-8 opacity-0";

    return (
      <PublicSurveyShell
        {...shellProps}
        progress={<SurveyProgress current={currentQuestion + 1} total={questions.length} />}
      >
        <div className={`transition-all duration-300 ease-out ${slideClass}`}>
          <SurveyQuestion
            key={currentQ.id}
            question={currentQ}
            answer={answers[currentQ.id]}
            onAnswer={(value) => handleAnswerChange(currentQ.id, value)}
            showCategory={campaign?.surveyType === "spiritual"}
          />
        </div>

        {validationError && (
          <AlertPane tone="danger" className="mt-6">
            {validationError}
          </AlertPane>
        )}

        <div className="mt-8 flex items-center gap-3">
          <IconButton
            variant="secondary"
            size="lg"
            icon={<MdChevronLeft className="h-6 w-6" />}
            label="Previous question"
            onClick={handlePrev}
            disabled={currentQuestion === 0 || isAnimating}
          />
          <Button
            size="lg"
            className="min-w-0 flex-1"
            rightIcon={<MdChevronRight aria-hidden="true" className="h-5 w-5" />}
            onClick={handleNext}
            disabled={isAnimating || submitting}
          >
            {currentQuestion === questions.length - 1 ? "Complete Survey" : "Continue"}
          </Button>
        </div>
      </PublicSurveyShell>
    );
  }

  if (flowState === "submitting") {
    return (
      <PublicSurveyShell {...shellProps} center>
        <div className="text-center">
          <Loader size="lg" label="Submitting" variant="inline" />
          <h1 className="mt-6 text-title">Submitting…</h1>
          <p className="mt-1 text-sm text-fg-muted">Just a moment</p>
        </div>
      </PublicSurveyShell>
    );
  }

  if (flowState === "completed") {
    const variant = isPathwaysSurvey
      ? "pathways"
      : campaign?.surveyType === "spiritual"
      ? "spiritual"
      : "general";

    const prayerCard = campaign?.enablePrayerRequests ? (
      <PrayerRequestCard
        showPhone={!isPathwaysSurvey}
        contact={prayerContactInfo}
        onContactChange={(field, value) => setPrayerContactInfo((prev) => ({ ...prev, [field]: value }))}
        request={prayerRequest}
        onRequestChange={setPrayerRequest}
        open={showPrayerForm}
        onOpen={() => setShowPrayerForm(true)}
        onClose={() => setShowPrayerForm(false)}
        submitted={prayerSubmitted}
        submitting={prayerSubmitting}
        onSubmit={handlePrayerSubmit}
      />
    ) : null;

    return (
      <PublicSurveyShell {...shellProps} panel={false}>
        <SurveyResults
          variant={variant}
          visitorName={visitorName}
          result={result}
          prayerCard={prayerCard}
          devotional={{
            data: emailSignupData,
            onChange: (field, value) => setEmailSignupData((prev) => ({ ...prev, [field]: value })),
            submitted: emailSignupSubmitted,
            onSubmit: handleEmailSignup,
          }}
          onRequestContact={() => setFlowState("contact")}
          onSkipContact={handleSkipContact}
        />
      </PublicSurveyShell>
    );
  }

  if (flowState === "contact") {
    return (
      <PublicSurveyShell {...shellProps} center>
        <ContactOptIn
          contact={contactInfo}
          onChange={(field, value) => setContactInfo((prev) => ({ ...prev, [field]: value }))}
          submitting={contactSubmitting}
          onSubmit={handleContactOptIn}
          onSkip={handleSkipContact}
        />
      </PublicSurveyShell>
    );
  }

  if (flowState === "done") {
    return (
      <PublicSurveyShell {...shellProps} center>
        <div className="py-4 text-center">
          <IconTile icon={MdCheckCircle} tone="success" size="xl" className="mx-auto" />
          <h1 className="mt-6 text-display">You're All Set</h1>
          <p className="mt-3 text-lg text-fg-muted">Thank you for sharing with us.</p>
          <p className="mt-6 text-sm text-fg-muted">You may close this window.</p>
        </div>
      </PublicSurveyShell>
    );
  }

  return null;
};

export default SpiritualSurveyPage;
